import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { AmbientLight, PointLight, RectAreaLight, RectAreaLightNode } from 'three/webgpu';
import { RectAreaLightTexturesLib } from 'three/addons/lights/RectAreaLightTexturesLib.js';

import { COLORS } from '../core/constants';
import { useAppStore } from '../store/appStore';
import { INTRO_FADE_SECONDS } from './Intro';

// The area light's BRDF lookup tables must be built and handed to the node
// renderer before the light is first compiled. This is the node equivalent of
// `RectAreaLightUniformsLib.init()` from the WebGL renderer.
RectAreaLightNode.setLTC(RectAreaLightTexturesLib.init());

const TITLE_AMBIENT = 1;
const STAGE_AMBIENT = 0;

/**
 * Candela. Modern three uses physically correct falloff, so the original's
 * intensity of 4 would barely register at this distance; the decay is also
 * softened from the inverse-square default to keep the card evenly lit.
 */
const TITLE_KEY_INTENSITY = 90;
const TITLE_KEY_DISTANCE = 40;
const TITLE_KEY_DECAY = 1.4;
/** Just in front of the title card, which sits two units out from the origin. */
const TITLE_KEY_POSITION: [number, number, number] = [0, 0, 5];

/**
 * The key light opens from a hairline to this height once the model is in.
 * Its area is what sets its power, so the height doubles as an intensity ramp.
 */
const STAGE_LIGHT_HEIGHT = 1.6;

/**
 * Nits. Modern three treats a rect area light's intensity as the radiance of
 * the emitting surface, so this is far higher than the value the legacy
 * renderer needed for the same result.
 */
const KEY_INTENSITY = 8;

const EASE = 4;

/**
 * Every light in the scene, for the whole of its life.
 *
 * The renderer compiles each material against the exact set of lights present,
 * so adding or removing one throws every pipeline away and builds it again on
 * the next frame — a stall exactly where the title card hands over to the
 * instrument if the card's light were to leave with it. The lights are all
 * mounted here from the warm-up onward, and only their intensities move.
 *
 * The title key is a point light in front of the card, and it burns down over
 * the same window as the card. The stage key is a wide, very short rect area
 * light that grazes the panel — the shape is what gives the instrument its long
 * specular streaks. It opens up as the instrument is revealed, while the flat
 * title ambience fades away.
 */
export function Lights() {
  const title = useRef<PointLight>(null);
  const key = useRef<RectAreaLight>(null);
  const ambient = useRef<AmbientLight>(null);
  const phase = useAppStore((state) => state.phase);
  const loaded = useAppStore((state) => state.keyboardLoaded);

  useFrame((_, delta) => {
    const ease = 1 - Math.exp(-EASE * delta);
    // The instrument is mounted from the warm-up onward, so being loaded no
    // longer means being on screen: the key light waits for the reveal.
    const revealed = loaded && (phase === 'dissolving' || phase === 'playing');
    const targetHeight = revealed ? STAGE_LIGHT_HEIGHT : 0;
    const targetAmbient = phase === 'playing' && revealed ? STAGE_AMBIENT : TITLE_AMBIENT;

    if (title.current && phase !== 'loading' && phase !== 'intro') {
      const step = (delta / INTRO_FADE_SECONDS) * TITLE_KEY_INTENSITY;
      title.current.intensity = Math.max(0, title.current.intensity - step);
    }
    if (key.current) {
      key.current.height += (targetHeight - key.current.height) * ease;
      // A rect area light emits from one face only, so it has to be aimed.
      key.current.lookAt(0, 0, 0);
    }
    if (ambient.current) {
      ambient.current.intensity += (targetAmbient - ambient.current.intensity) * ease;
    }
  });

  return (
    <>
      <pointLight
        ref={title}
        args={[COLORS.white, TITLE_KEY_INTENSITY, TITLE_KEY_DISTANCE, TITLE_KEY_DECAY]}
        position={TITLE_KEY_POSITION}
      />
      <rectAreaLight ref={key} args={[0xffffff, KEY_INTENSITY, 70, 0]} position={[0, 5, 4]} />
      <ambientLight ref={ambient} args={[0x2020a0, TITLE_AMBIENT]} />
    </>
  );
}
